import globalWindow from '@shared/core/globals';
import { useEffect, useState } from 'react';
import { useTheme } from '../styledComponents';
import type { MediaQuery, Theme } from '../themes/types';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import type { MediaScreen } from '@shared/utils/media/DefaultMediaMatchProvider';
import { useDefaultMediaMatchContext } from '@shared/utils/media/DefaultMediaMatchProvider';
import { mediaScreenWidths } from '@shared/utils/media/mediaScreenWidths.constants';
import { scheduleMediaQueryUpdate } from '@shared/utils/media/scheduleMediaQueryUpdate';

type Query = keyof MediaQuery | ((theme: Theme) => string);
type Orientation = '(orientation: portrait)' | '(orientation: landscape)';

type UseMediaQueryOptions = {
  defaultMatches?: boolean;
};

// Regex is unit-agnostic — extracts numeric values only. This assumes all
// media queries use em or rem (which are numerically equivalent at root font size).
// All joykit theme queries use em; useResponsive breakpoints use rem. If a query
// ever uses px, the comparison would be incorrect — add a unit check if that arises.
const MIN_WIDTH_REGEX = /min-width:\s*([\d.]+)/g;
const MAX_WIDTH_REGEX = /max-width:\s*([\d.]+)/g;
const HAS_WIDTH_CONDITION_REGEX = /min-width|max-width/;

/**
 * Simulate a CSS media query match against a representative viewport width.
 *
 * Parses min-width and max-width conditions from the query string and evaluates
 * them against the representative viewport width for the given screen type.
 *
 * Returns false for queries without width conditions (e.g., orientation)
 * since they can't be simulated from a screen category alone.
 */
export function simulateMediaQuery(query: string, defaultScreen: MediaScreen): boolean {
  const screenWidthEm = mediaScreenWidths[defaultScreen];

  // Reset lastIndex since global regexes are stateful across exec calls
  MIN_WIDTH_REGEX.lastIndex = 0;
  MAX_WIDTH_REGEX.lastIndex = 0;

  const minWidths: number[] = [];
  const maxWidths: number[] = [];

  let execMatch: RegExpExecArray | null;
  while ((execMatch = MIN_WIDTH_REGEX.exec(query)) !== null) {
    minWidths.push(parseFloat(execMatch[1]));
  }
  while ((execMatch = MAX_WIDTH_REGEX.exec(query)) !== null) {
    maxWidths.push(parseFloat(execMatch[1]));
  }

  if (minWidths.length === 0 && maxWidths.length === 0) {
    return false;
  }

  for (const minWidth of minWidths) {
    if (screenWidthEm < minWidth) return false;
  }
  for (const maxWidth of maxWidths) {
    if (screenWidthEm > maxWidth) return false;
  }

  return true;
}

export const useMediaQuery = (queryInput: Query | Orientation, options: UseMediaQueryOptions = {}) => {
  const theme = useTheme();

  // strict: false — returns undefined when no provider is present (tests, non-app contexts)
  const mediaMatchContext = useDefaultMediaMatchContext();

  // matchMedia is a Web API - ensure useMediaQuery triggers when window is available.
  const isMounted = useIsMounted();

  // Access matchMedia at call time (not module level) so that:
  // 1. Tests can mock window.matchMedia per-test without module re-loading
  // 2. The value correctly reflects the runtime environment at hook invocation
  const matchMedia = globalWindow.matchMedia;

  let query: string = typeof queryInput === 'function' ? queryInput(theme) : queryInput;
  query = query.replace(/^@media( ?)/m, '');

  const [match, setMatch] = useState<boolean>(() => {
    if (matchMedia) {
      return matchMedia(query).matches;
    }

    // SSR: simulate using defaultScreen from server-side UA detection.
    // simulateMediaQuery returns false for non-width queries (e.g., orientation)
    // since they can't be derived from a screen category — fall through to
    // defaultMatches in that case to preserve caller control.
    if (mediaMatchContext?.defaultScreen) {
      const simulated = simulateMediaQuery(query, mediaMatchContext.defaultScreen);
      if (simulated) return true;
      // Simulation returned false — could be a genuine non-match OR an unsimulatable query.
      // Check if the query has width conditions; if so, the false is a real result.
      if (HAS_WIDTH_CONDITION_REGEX.test(query)) return false;
      // Non-width query — fall through to defaultMatches
    }

    return !!options.defaultMatches;
  });

  useEffect(() => {
    if (!isMounted || !matchMedia) {
      return;
    }

    const queryList = matchMedia(query);
    setMatch(queryList.matches);

    let isSubscribed = true;
    const handleChange = () => {
      scheduleMediaQueryUpdate(() => {
        if (isSubscribed) setMatch(queryList.matches);
      });
    };

    queryList.addListener(handleChange);
    return () => {
      isSubscribed = false;
      queryList.removeListener(handleChange);
    };
  }, [query, isMounted]);

  return match;
};
